import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import { AppContent } from '@/components/AppContent';
import { MaintenanceMode } from '@/components/common/MaintenanceMode';
import { maintenanceService, MaintenanceStatus } from '@/services/maintenanceService';
import { UserLevel } from '@/types/auth.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

export {};

export const AppContentWrapper: React.FC = () => {
  const { utilizator } = useAuth();
  const { activeSection, setActiveSection } = useSectionContext();
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectionMismatch, setSectionMismatch] = useState<string | null>(null);

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  const checkMaintenanceStatus = async () => {
    try {
      const status = await maintenanceService.getMaintenanceStatus();
      setMaintenanceStatus(status);
    } catch (error) {
      console.error('Failed to check maintenance status:', error);
      // Assume no maintenance mode if we can't check
      setMaintenanceStatus({
        maintenance_mode: false,
        maintenance_message: '',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMaintenance = async () => {
    if (!utilizator) return;

    try {
      const newStatus = !maintenanceStatus?.maintenance_mode;
      await maintenanceService.toggleMaintenanceMode({
        enable: newStatus,
        message: maintenanceStatus?.maintenance_message || 'Sistemul este în mentenanță. Vă rugăm să încercați din nou mai târziu.'
      });

      // Refresh maintenance status
      await checkMaintenanceStatus();
    } catch (error) {
      console.error('Failed to toggle maintenance mode:', error);
    }
  };

  // Show loading spinner while checking maintenance status
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show maintenance mode if active and user is not an admin
  if (maintenanceStatus?.maintenance_mode && utilizator) {
    const userLevel = utilizator?.user_level_id || utilizator?.user_level?.id || 0;
    const isAdmin = userLevel === UserLevel.ADMINISTRATOR;

    // Allow admins to see the normal interface even in maintenance mode
    if (isAdmin) {
      return <AppContent />;
    }

    // Show maintenance mode for regular users
    return (
      <MaintenanceMode
        message={maintenanceStatus.maintenance_message}
        isAdmin={isAdmin}
        onToggleMaintenance={handleToggleMaintenance}
      />
    );
  }

  // Auto-set section for hospital users based on their assigned_section.
  // Compared against the expected value (not just "unset") so a section
  // change - a different user logging in, or an admin reassigning the
  // current user's section - takes effect without a browser restart.
  if (utilizator) {
    const userLevel = utilizator?.user_level_id || utilizator?.user_level?.id || 0;
    const isHospitalUser = userLevel === UserLevel.HOSPITAL;
    const expectedSection = utilizator.assigned_section || 'chd';

    if (isHospitalUser && activeSection !== expectedSection) {
      setActiveSection(expectedSection);

      const loginTab = sessionStorage.getItem('remco_login_tab');
      if (loginTab && loginTab !== expectedSection) {
        const sectionLabel = expectedSection === 'chd' ? 'MCC' : 'Prematuri';
        setSectionMismatch(`Contul dvs. este asociat registrului ${sectionLabel}. Ați fost redirecționat automat.`);
        setTimeout(() => setSectionMismatch(null), 5000);
      }
      sessionStorage.removeItem('remco_login_tab');

      return null;
    }
  }

  // Show normal app content
  return (
    <>
      {sectionMismatch && (
        <div className="fixed top-4 left-1/2 z-50 -translate-x-1/2">
          <Alert className="border-blue-200 bg-blue-50 text-blue-800 shadow-lg">
            <Info className="h-4 w-4" />
            <AlertDescription>{sectionMismatch}</AlertDescription>
          </Alert>
        </div>
      )}
      <AppContent />
    </>
  );
};